import React, {useContext, useEffect, useMemo} from 'react';
import {useParams} from 'react-router-dom';
import Layout from './Layout';
import {RowData, Loader, GoogleMap} from '../../components';
import {connect} from 'react-redux';
import * as Actions from '../../store/actions';
import EmptyState from '../../components/EmptyState';
import {
  base_address_marker,
  corruptedFile,
  false_address_marker,
  true_address_marker,
} from '../../assets/images/images';
import {PageContext} from '../../helpers/context';
import {ImageDisplaySection} from './components';
import {ImageModal} from '../../components/ImageModal';

function Address({
  getAddress,
  getIpDeviceCheck,
  verifications: {address, loading},
}) {
  const {slug} = useParams();
  const [file, setFile] = React.useState('');

  const {setPageName} = useContext(PageContext);
  useEffect(() => {
    setPageName('verifications');
  }, [setPageName]);

  useEffect(() => {
    const sessionId = localStorage.getItem('sessionId');
    const verificationId = localStorage.getItem('verificationId');

    getIpDeviceCheck(slug || verificationId);
    getAddress(slug || sessionId);
  }, [getAddress, getIpDeviceCheck, slug]);

  const DIGITAL_ADDRESS_DATA = useMemo(
    () => (!loading && address && address?.data?.base) || {},
    [address, loading],
  );

  const subAddress = useMemo(
    () =>
      (Array.isArray(address?.data?.sub_addresses) &&
        `Lon: ${address.data?.sub_addresses[0]?.longitude || 'not tracked.'},   
         Lat: ${address.data?.sub_addresses[0]?.latitude || 'not tracked.'}`) ||
      'N/A',
    [address],
  );
  const ADDRESS_DATA = useMemo(
    () => [
      {leftText: 'Submitted Address', rightText: DIGITAL_ADDRESS_DATA?.name},
      {
        leftText: 'State',
        rightText: DIGITAL_ADDRESS_DATA?.state || 'N/A',
      },
      {
        leftText: 'Local Government Area',
        rightText: DIGITAL_ADDRESS_DATA?.lga || 'N/A',
      },
      {
        leftText: 'Landmark',
        rightText: DIGITAL_ADDRESS_DATA?.landmark || 'N/A',
      },
      {
        leftText: 'Geo-location',
        rightText: subAddress || 'N/A',
      },
    ],
    [DIGITAL_ADDRESS_DATA, subAddress],
  );
  const UTILITY_BILL_ADDRESS = useMemo(
    () => [
      {
        leftText: 'Address',
        rightText: DIGITAL_ADDRESS_DATA?.utility_bill_address || '-',
      },
      {
        leftText: 'Date',
        rightText: DIGITAL_ADDRESS_DATA?.utility_bill_date || '-',
      },
    ],
    [DIGITAL_ADDRESS_DATA],
  );

  const subAddresses = address?.data?.sub_addresses;
  const isUtilityDataAvailable = DIGITAL_ADDRESS_DATA?.utility_bill_enabled;
  const isLiveLocationEnabled = DIGITAL_ADDRESS_DATA?.live_location_enabled;

  const formatAddresses = () => {
    const addresses = subAddresses && [...subAddresses, DIGITAL_ADDRESS_DATA];
    return addresses?.map(address => ({
      lat: +address?.latitude,
      lng: +address?.longitude,
      name: address?.name ? address?.name : 'Origin Point (Fixed Anchor)',
      markerImage: address.name
        ? base_address_marker
        : address.match
        ? true_address_marker
        : false_address_marker,
    }));
  };

  const center = {
    lat: Number(DIGITAL_ADDRESS_DATA?.longitude || 0),
    lng: Number(DIGITAL_ADDRESS_DATA?.latitude || 0),
  };

  return (
    <Layout>
      <div>
        {!loading && address && subAddresses?.length === 0 ? (
          <>
            <EmptyState
              body={'We did not find any address information on this person'}
              src={corruptedFile}
              noBtn
              height={45}
            />
          </>
        ) : loading ? (
          <Loader />
        ) : (
          <>
            <p className="font-medium text-grey">Address</p>
            <ImageModal file={file} setFile={setFile} />
            <div className="flex flex-col mt-4">
              {ADDRESS_DATA.map(({leftText, rightText}, i) => (
                <RowData key={i} leftText={leftText} rightText={rightText} />
              ))}
            </div>
            <div className="mt-10">
              <div className="flex items-center justify-between">
                <p className="font-medium text-grey">Map</p>
                <div className="uppercase pt-[5px] pb-[3px] px-2 text-xs text-status-400 font-medium bg-disabled rounded-full w-fit flex items-start gap-[2px]">
                  {address?.data?.percent}% match
                </div>
              </div>
              <div className="relative">
                <GoogleMap
                  center={center}
                  zoom={8}
                  multipleMarkers={formatAddresses()}
                />
              </div>
            </div>

            {isUtilityDataAvailable && (
              <>
                <p className="font-medium text-grey mt-10">Utility Bill</p>
                <div className="flex flex-col mt-4">
                  {UTILITY_BILL_ADDRESS.map(({leftText, rightText}, i) => (
                    <RowData
                      key={i}
                      leftText={leftText}
                      rightText={rightText}
                    />
                  ))}
                </div>
                {DIGITAL_ADDRESS_DATA?.utility_bill_image && (
                  <ImageDisplaySection
                    customFunc={setFile}
                    imagesData={[
                      {
                        src: DIGITAL_ADDRESS_DATA?.utility_bill_image,
                        alt: 'Utility Bill',
                        id: 'utility_bill_image',
                      },
                    ]}
                  />
                )}
              </>
            )}

            {isLiveLocationEnabled && (
              <>
                {DIGITAL_ADDRESS_DATA?.front_of_house_image && (
                  <ImageDisplaySection
                    customFunc={setFile}
                    imagesData={[
                      {
                        src: DIGITAL_ADDRESS_DATA?.front_of_house_image,
                        alt: 'Front of House',
                        id: 'front_of_house_image',
                        desc: 'front of house from within the compound',
                      },
                      {
                        src: DIGITAL_ADDRESS_DATA?.outside_gate_image,
                        alt: 'Outside Gate',
                        id: 'outside_gate_image',
                        desc: 'IMAGE OF HOUSE SHOWING THE GATE & HOUSE NO.',
                      },
                    ]}
                  />
                )}
                {DIGITAL_ADDRESS_DATA?.street_image && (
                  <ImageDisplaySection
                    customFunc={setFile}
                    imagesData={[
                      {
                        src: DIGITAL_ADDRESS_DATA?.street_image,
                        alt: 'Street',
                        id: 'street_image',
                        desc: 'IMAGE OF street showing parts of the house ',
                      },
                    ]}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

export default connect(state => state, Actions)(Address);
